/**
 * Web Studio Event Controller namespace
 * @exports src/client/facade
 * @namespace input
 */

import events from "_src/engine/events";

import renderGraph from "_src/render_graph/render_graph";
import {screenUtils} from "_src/client/utils";
import inputCollector from "_src/client/input";
import windowManager from "_src/client/window_manager";

let zoomTimeout = null;
let zoomIsActive = false;

const input = {

  /**
   * Start camera control
   * @memberof input
   * @param {Number} clientX Client X coordinate
   * @param {Number} clientY Client Y coordinate
   * @method controlStart
   */
  controlStart(clientX, clientY) {
    const screenPoint = inputCollector.clientToPhysical(clientX, clientY);
    events.emit("control_start", screenPoint);
  },
  /**
   * Lock cursor by viewcube
   * @memberof input
   * @param {Number} clientX Client X coordinate
   * @param {Number} clientY Client Y coordinate
   * @method viewcubeLockCursor
   */
  viewcubeLockCursor(clientX, clientY) {
    const screenPoint = inputCollector.clientToPhysical(clientX, clientY);
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const viewCubePass = mainWindow.renderGraph.getPass(renderGraph.VIEWCUBE);
    const billboardScene = viewCubePass.getScenes()[2];
    const billboardCamera = billboardScene.camera;
    const ndcCoord = screenUtils.toScreen(mainWindow.getViewport(), billboardCamera.viewport,
        screenPoint.screenX, screenPoint.screenY);

    const camera = mainWindow.renderGraph.getWorkspaceScene().camera;
    const recalculatedScreenCoord = screenUtils.fromScreen(mainWindow.getViewport(), camera.viewport,
        ndcCoord.x, ndcCoord.y);

    events.emit("control_start", { screenX : Math.round(recalculatedScreenCoord.x),
        screenY : Math.round(recalculatedScreenCoord.y) });
  },
  /**
   * Move camera control
   * @memberof input
   * @param {Number} deltaX Client X delta coordinate
   * @param {Number} deltaY Client Y delta coordinate
   * @method controlMove
   */
  controlMove(deltaX, deltaY) {
    const pixelRatio = screenUtils.getPixelRatio();
    let movementX = pixelRatio * deltaX;
    let movementY = -pixelRatio * deltaY;
    events.emit("control_move", {movementX, movementY});
  },
  /**
   * Pan camera control
   * @memberof input
   * @param {Number} deltaX Client X delta coordinate
   * @param {Number} deltaY Client Y delta coordinate
   * @method controlPan
   */
  controlPan(deltaX, deltaY) {
    const pixelRatio = screenUtils.getPixelRatio();
    let movementX = pixelRatio * deltaX;
    let movementY = pixelRatio * deltaY;
    events.emit("control_pan", {movementX, movementY});
  },
  /**
   * Stop camera control
   * @memberof input
   * @method controlEnd
   */
  controlEnd() {
    events.emit("control_end");
  },
  /**
   * Zoom camera control
   * @memberof input
   * @param {Number} clientX Client X coordinate
   * @param {Number} clientY Client Y coordinate
   * @param {Number} delta Zoom step
   * @method controlZoom
   */
  controlZoom(clientX, clientY, delta) {
    const screenPoint = inputCollector.clientToPhysical(clientX, clientY);

    if (!zoomIsActive) {
      events.emit("control_zoom_start", screenPoint);
    }

    zoomIsActive = true;
    clearTimeout(zoomTimeout);
    zoomTimeout = setTimeout(() => {
      zoomIsActive = false;
      events.emit("control_zoom_end", screenPoint);
    }, 250);

    events.emit("control_zoom", {screenX : screenPoint.screenX, screenY : screenPoint.screenY, delta});
  },
  /**
   * Set camera control target
   * @memberof input
   * @param {Number} clientX Client X coordinate
   * @param {Number} clientY Client Y coordinate
   * @method controlSetTarget
   */
  controlSetTarget(clientX, clientY) {
    const screenPoint = inputCollector.clientToPhysical(clientX, clientY);
    events.emit("control_set_target", screenPoint);
  },
  /**
   * Start applying selected manipulator
   * @memberof input
   * @param {Number} clientX Client X coordinate
   * @param {Number} clientY Client Y coordinate
   * @method manipulatorStart
   */
  manipulatorStart(clientX, clientY) {
    const screenPoint = inputCollector.clientToPhysical(clientX, clientY);
    events.emit("manipulator_start", screenPoint);
  },
  /**
   * Move selected manipulator
   * @memberof input
   * @param {Number} clientX Client X coordinate
   * @param {Number} clientY Client Y coordinate
   * @param {Boolean} active Is manipulator acvive or not
   * @method manipulatorMove
   */
  manipulatorMove(clientX, clientY, active) {
    const screenPoint = inputCollector.clientToPhysical(clientX, clientY);
    events.emit("manipulator_move", {screenX : screenPoint.screenX, screenY : screenPoint.screenY, active});
  },
  /**
   * Stop applying selected manipulator
   * @memberof input
   * @param {Number} clientX Client X coordinate
   * @param {Number} clientY Client Y coordinate
   * @method manipulatorStart
   */
  manipulatorEnd(clientX, clientY) {
    const screenPoint = inputCollector.clientToPhysical(clientX, clientY);
    events.emit("manipulator_end", screenPoint);
  },
  /**
   * Pass mouse interaction into the engine
   * @memberof input
   * @param {Number} clientX Client X coordinate
   * @param {Number} clientY Client Y coordinate
   * @param {Number} button Mouse button emitting the event
   * @param {Number} buttons Pressed mouse button
   * @method onMouseDown
   */
  onMouseDown(clientX, clientY, button, buttons) {
    inputCollector.mousedownHandler(clientX, clientY, button, buttons);
  },
  /**
   * Pass mouse interaction into the engine
   * @memberof input
   * @param {Number} clientX Client X coordinate
   * @param {Number} clientY Client Y coordinate
   * @param {Number} button Mouse button emitting the event
   * @param {Number} buttons Pressed mouse button
   * @method onMouseMove
   */
  onMouseMove(clientX, clientY, button, buttons) {
    inputCollector.mousemoveHandler(clientX, clientY, button, buttons);
  },
  /**
   * Pass mouse interaction into the engine
   * @memberof input
   * @param {Number} clientX Client X coordinate
   * @param {Number} clientY Client Y coordinate
   * @param {Number} button Mouse button emitting the event
   * @param {Number} buttons Pressed mouse button
   * @method onMouseUp
   */
  onMouseUp(clientX, clientY, button, buttons) {
    inputCollector.mouseupHandler(clientX, clientY, button, buttons);
  },
};

export default input;
